/* @jsx h */
import { defineComponent, ref, h, onMounted, computed } from "vue";
import { useAuth } from "@app/vue/providers/Auth";
import { setTitle } from "@lemmonhq/orders";
import {
  SectionedBody,
  type SectionedBodySection,
  useLocalization,
} from "@lemmonhq/uikit";
import { AppSubpage, AppSubpageContent } from "@app/vue/components/AppSubpage";
import { AppSubpageHeader } from "@app/vue/components/AppSubpageHeader";
import { api } from "@app/data/api";
import type { RestaurantProfileData } from "./types";
import { BusinessInfoSection } from "./components/BusinessInfoSection";
import { ContactInfoSection } from "./components/ContactInfoSection";
import { RegionalSettingsSection } from "./components/RegionalSettingsSection";
import { SocialLinksSection } from "./components/SocialLinksSection";
import { RestaurantFeaturesSection } from "./components/RestaurantFeaturesSection";
import { FeatureFlagsSettings } from "../ConfigSettings/FeatureFlags";
import type { LemmonFeatureFlags } from "@lemmonhq/types";

export const ProfileSettings = defineComponent({
  name: "ProfileSettings",
  setup() {
    const { t } = useLocalization();
    const auth = useAuth();
    const saving = ref(false);
    const saveSuccess = ref(false);
    const saveError = ref("");
    const sections = computed<SectionedBodySection[]>(() => [
      {
        id: "business-information",
        label: t("settings.profile.business_information"),
        icon: "business",
      },
      {
        id: "digital-presence",
        label: t("settings.profile.digital_presence"),
        icon: "share",
      },
      {
        id: "contact-information",
        label: t("settings.profile.contact_information"),
        icon: "contact_mail",
      },
      {
        id: "regional-settings",
        label: t("settings.profile.regional_settings"),
        icon: "language",
      },
      {
        id: "feature-flags",
        label: t("settings.profile.feature_flags"),
        icon: "flag",
      },
      {
        id: "restaurant-features",
        label: t("settings.profile.restaurant_features"),
        icon: "tune",
      },
    ]);

    const restaurant = auth.user.value?.restaurant;
    console.log(restaurant);
    const defaultSocials = {
      facebook: restaurant?.socials?.facebook || "",
      instagram: restaurant?.socials?.instagram || "",
      linkedin: restaurant?.socials?.linkedin || "",
      website: restaurant?.socials?.website || "",
    };
    const defaultGeolocation = { ...(restaurant?.geolocation || {}) };

    const formData = ref<Partial<RestaurantProfileData>>({
      name: restaurant?.name || "",
      email: restaurant?.email || "",
      phone: restaurant?.phone || "",
      contact_person: restaurant?.contact_person || "",
      address: restaurant?.address || "",
      slug: restaurant?.slug || "",
      company_registration: restaurant?.company_registration || "",
      timezone: restaurant?.timezone || "Europe/Zurich",
      locale: restaurant?.locale || "fr_CH",
      currency: restaurant?.currency || "CHF",
      jurisdiction: restaurant?.jurisdiction || "CH",
      geolocation: defaultGeolocation,
      description: restaurant?.description || "",
      socials: defaultSocials,
      feature_flags: restaurant?.feature_flags ?? {},
    });

    const serializedFlagsInitial = JSON.stringify(
      restaurant?.feature_flags ?? {},
    );

    const hasChanges = computed(() => {
      if (!restaurant) return false;

      const geolocationChanged =
        (formData.value.geolocation?.lat || null) !==
          (restaurant.geolocation?.lat || null) ||
        (formData.value.geolocation?.lng || null) !==
          (restaurant.geolocation?.lng || null) ||
        (formData.value.geolocation?.address || "") !==
          (restaurant.geolocation?.address || "");

      const socialsChanged =
        (formData.value.socials?.facebook || "") !==
          (restaurant.socials?.facebook || "") ||
        (formData.value.socials?.instagram || "") !==
          (restaurant.socials?.instagram || "") ||
        (formData.value.socials?.linkedin || "") !==
          (restaurant.socials?.linkedin || "") ||
        (formData.value.socials?.website || "") !==
          (restaurant.socials?.website || "");

      return (
        formData.value.name !== restaurant.name ||
        formData.value.email !== restaurant.email ||
        formData.value.phone !== restaurant.phone ||
        formData.value.contact_person !== restaurant.contact_person ||
        formData.value.address !== restaurant.address ||
        formData.value.slug !== restaurant.slug ||
        formData.value.company_registration !==
          restaurant.company_registration ||
        formData.value.timezone !== restaurant.timezone ||
        formData.value.locale !== restaurant.locale ||
        formData.value.currency !== restaurant.currency ||
        formData.value.jurisdiction !== restaurant.jurisdiction ||
        formData.value.description !== (restaurant.description || "") ||
        geolocationChanged ||
        socialsChanged ||
        JSON.stringify(formData.value.feature_flags ?? {}) !==
          serializedFlagsInitial
      );
    });

    const updateField = <K extends keyof RestaurantProfileData>(
      field: K,
      value: RestaurantProfileData[K],
    ) => {
      formData.value = {
        ...formData.value,
        [field]: value,
      };
      saveSuccess.value = false;
      saveError.value = "";
    };

    const handleSubmit = async (e: Event) => {
      e.preventDefault();
      if (!hasChanges.value) return;

      saving.value = true;
      saveError.value = "";
      saveSuccess.value = false;

      try {
        await api.updateRestaurantProfile(formData.value);

        // Update auth context with new data
        if (auth.user.value) {
          auth.setUser({
            ...auth.user.value,
            restaurant: {
              ...auth.user.value.restaurant,
              ...formData.value,
            },
          });
        }

        saveSuccess.value = true;
        setTimeout(() => {
          saveSuccess.value = false;
        }, 3000);
      } catch (error: any) {
        console.error("Failed to save profile:", error);
        saveError.value = error.message || "Failed to save changes";
      } finally {
        saving.value = false;
      }
    };

    return () => (
      <div class="flex flex-1 flex-col">
        <AppSubpageHeader
          icon="storefront"
          title={t("settings.profile.title")}
          description={t("settings.profile.desc")}
        >
          {{
            actions: () => (
              <div class="flex items-center gap-3">
                {saveSuccess.value && (
                  <div class="flex items-center gap-2 text-success text-sm font-medium">
                    <span class="material-symbols-outlined text-lg">
                      check_circle
                    </span>
                    {t("settings.profile.saved_successfully")}
                  </div>
                )}
                <button
                  type="submit"
                  form="profile-form"
                  disabled={!hasChanges.value || saving.value}
                  class="btn btn-primary btn-sm gap-2 shadow-lg"
                >
                  {saving.value ? (
                    <>
                      <span class="loading loading-spinner loading-sm"></span>
                      {t("settings.profile.saving")}
                    </>
                  ) : (
                    <>
                      <span class="material-symbols-outlined text-lg">
                        save
                      </span>
                      {t("settings.profile.save_changes")}
                    </>
                  )}
                </button>
              </div>
            ),
          }}
        </AppSubpageHeader>

        <AppSubpage>
          <AppSubpageContent noPadding class="p-0 pb-4 lg:p-6">
            <form id="profile-form" onSubmit={handleSubmit} class="mx-auto">
              {/* Error Alert */}
              {saveError.value && (
                <div class="alert alert-error mb-6 shadow-lg">
                  <span class="material-symbols-outlined">error</span>
                  <span>{saveError.value}</span>
                </div>
              )}

              <SectionedBody
                sections={sections.value}
                ariaLabel={t("settings.profile.title")}
              >
                {{
                  "business-information": () => (
                    <BusinessInfoSection
                      data={formData.value}
                      onUpdate={updateField}
                    />
                  ),
                  "digital-presence": () => (
                    <SocialLinksSection
                      data={formData.value}
                      onUpdate={updateField}
                    />
                  ),
                  "contact-information": () => (
                    <ContactInfoSection
                      data={formData.value}
                      onUpdate={updateField}
                    />
                  ),
                  "regional-settings": () => (
                    <RegionalSettingsSection
                      data={formData.value}
                      onUpdate={updateField}
                    />
                  ),
                  "feature-flags": () => (
                    <FeatureFlagsSettings
                      flags={
                        (formData.value.feature_flags as LemmonFeatureFlags) ??
                        {}
                      }
                      onUpdate={(flags) => updateField("feature_flags", flags)}
                    />
                  ),
                  "restaurant-features": () => <RestaurantFeaturesSection />,
                }}
              </SectionedBody>
            </form>
          </AppSubpageContent>
        </AppSubpage>
      </div>
    );
  },
});
